import { useState, useEffect } from "react";
import { ProjectExtendedDetailService } from "shared/services/project";
import { handleErrors, formatDateTime24h } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";
import FeedbackRequestSkeleton from "shared/components/common/feedbackRequestSkeleton";
import type { Document, InspirationFile } from "shared/types/projectOverview";
import { Icons } from "assets";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomGalleryView from "shared/components/common/customGalleryView";
import classNames from "classnames";
import styles from "./style.module.scss";

interface ProjectOverviewProps {
  projectId?: number;
  contractDocuments?: Document[];
  questionnaireDocuments?: Document[];
  inspirationFiles?: InspirationFile[];
  onDownloadDocument?: (fileName: string) => void;

  showContract?: boolean;
  showQuestionnaire?: boolean;
  showInspirationFiles?: boolean;
}

const ProjectOverview = ({
  projectId,
  contractDocuments: propContractDocuments,
  questionnaireDocuments: propQuestionnaireDocuments,
  inspirationFiles: propInspirationFiles,
  onDownloadDocument,

  showContract = true,
  showQuestionnaire = true,
  showInspirationFiles = true,
}: ProjectOverviewProps) => {
  const [loading, setLoading] = useState(false);
  const [contractDocuments, setContractDocuments] = useState<Document[]>([]);
  const [questionnaireDocuments, setQuestionnaireDocuments] = useState<
    Document[]
  >([]);
  const [inspirationFiles, setInspirationFiles] = useState<InspirationFile[]>(
    [],
  );
  const [revisionInspirations, setRevisionInspirations] = useState<
    InspirationFile[]
  >([]);

  const isImageFile = (file: InspirationFile): boolean => {
    if (file.type) return file.type === "image";
    if (file.mime) return file.mime.startsWith("image/");
    const path = (file.image || "").toLowerCase();
    if (!path) return true;
    return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)(\?|$)/.test(path);
  };

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryItems, setGalleryItems] = useState<
    Array<{ id: string; src: string; name: string; alt: string }>
  >([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

  const openImageGallery = (
    pool: InspirationFile[],
    clickedFile: InspirationFile,
  ) => {
    const imagesOnly = pool.filter(isImageFile);
    const items = imagesOnly.map((f) => ({
      id: String(f.id),
      src: f.image || "",
      name: f.label || "",
      alt: f.label || "",
    }));
    const idx = imagesOnly.findIndex((f) => f.id === clickedFile.id);
    setGalleryItems(items);
    setGalleryInitialIndex(idx >= 0 ? idx : 0);
    setGalleryOpen(true);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
    } else {
      setContractDocuments(propContractDocuments || []);
      setQuestionnaireDocuments(propQuestionnaireDocuments || []);
      setInspirationFiles(propInspirationFiles || []);
    }
  }, [
    projectId,
    propContractDocuments,
    propQuestionnaireDocuments,
    propInspirationFiles,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchProjectDetails = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const response = await ProjectExtendedDetailService(projectId);

      if (response.status === 200) {
        const data = response.data?.data || response.data;

        const contracts: Document[] = [];
        if (data.contract) {
          contracts.push({
            id: data.contract.project_contract_id?.toString() || "1",
            name: data.contract.path?.split("/").pop() || "Contract.pdf",
            path: data.contract.path,
          });
        }
        setContractDocuments(contracts);

        const questionnaires: Document[] = [];
        if (data.questionnaire) {
          questionnaires.push({
            id: data.questionnaire.project_questionnaire_id?.toString() || "1",
            name:
              data.questionnaire.path?.split("/").pop() || "Questionnaire.pdf",
            path: data.questionnaire.path,
          });
        }
        setQuestionnaireDocuments(questionnaires);

        const mappedInspirations: InspirationFile[] = (
          data.inspirations || []
        ).map((inspiration: any) => ({
          id: inspiration.project_inspiration_id?.toString() || "",
          image: inspiration.path || "",
          label: inspiration.label || "",
          additionalNotes: inspiration.additional_notes || "",
          name: inspiration.name,
          type: inspiration.type,
          mime: inspiration.mime,
          createdAt: inspiration.created_at,
        }));
        setInspirationFiles(mappedInspirations);

        const mappedRevisionInspirations: InspirationFile[] = (
          data.revision_inspirations || []
        ).map((ri: any) => ({
          id: ri.revision_inspiration_id?.toString() || "",
          image: ri.path || "",
          label: ri.label || "",
          additionalNotes: ri.additional_notes || "",
          name: ri.name,
          type: ri.type,
          mime: ri.mime,
          createdAt: ri.created_at,
          revisionId: ri.revision_id?.toString() || "",
          revisionTitle: ri.revision_title || "",
        }));
        setRevisionInspirations(mappedRevisionInspirations);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDocument = (path?: string, fileName?: string) => {
    if (onDownloadDocument && fileName) {
      onDownloadDocument(fileName);
      return;
    }

    if (!path) {
      toastMessage("error", "Document path is not available");
      return;
    }

    window.open(path, "_blank");
  };

  if (loading) {
    return <FeedbackRequestSkeleton />;
  }

  return (
    <div className={classNames(styles.sectionContainer, "pt-3")}>
      <div className="flex flex-col md:gap-6 gap-4">
        {showContract && (
          <div className={styles.section}>
            <CustomPageHeading title="Contract" size="medium" />
            {contractDocuments && contractDocuments.length > 0 ? (
              <div className={styles.documentsList}>
                {contractDocuments.map((doc) => (
                  <div key={doc.id} className={styles.documentItem}>
                    <div className={styles.documentInfo}>
                      <Icons.FilePdfIcon className={styles.documentIcon} />
                      <span className={styles.documentName}>{doc.name}</span>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleOpenDocument(doc.path, doc.name)}
                      aria-label={`Open ${doc.name}`}
                    >
                      <Icons.Download className={styles.downloadIcon} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <span className={styles.emptyText}>No Contract Uploaded</span>
              </div>
            )}
          </div>
        )}

        {showQuestionnaire && (
          <div className={styles.section}>
            <CustomPageHeading title="Questionnaire" size="medium" />
            {questionnaireDocuments && questionnaireDocuments.length > 0 ? (
              <div className={styles.documentsList}>
                {questionnaireDocuments.map((doc) => (
                  <div key={doc.id} className={styles.documentItem}>
                    <div className={styles.documentInfo}>
                      <Icons.TabOverview className={styles.documentIcon} />
                      <span className={styles.documentName}>{doc.name}</span>
                    </div>
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleOpenDocument(doc.path, doc.name)}
                      aria-label={`Open ${doc.name}`}
                    >
                      <Icons.Download className={styles.downloadIcon} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <span className={styles.emptyText}>
                  No Questionnaire Uploaded
                </span>
              </div>
            )}
          </div>
        )}

        {showInspirationFiles &&
          inspirationFiles &&
          inspirationFiles.length > 0 && (
            <div className={styles.section}>
              <CustomPageHeading title="Inspiration Files" size="medium" />
              <div
                className={classNames(
                  styles.inspirationFilesList,
                  "grid md:grid-cols-2 grid-cols-1 lg:gap-6 md:gap-5 gap-4 mt-3",
                )}
              >
                {inspirationFiles &&
                  inspirationFiles.map((file) => (
                    <div
                      key={file.id}
                      className={classNames(styles.inspirationFileItem, "")}
                    >
                      <div className="flex flex-col">
                        <div className="flex md:items-start items-center gap-4">
                          <span className={styles.imgContainer}>
                            {isImageFile(file) ? (
                              <img
                                src={file.image}
                                alt={file.label}
                                onClick={() =>
                                  openImageGallery(inspirationFiles, file)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            ) : (
                              <a
                                href={file.image}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.fileTypeThumb}
                                title="Click to view file"
                              >
                                <Icons.FilePdfIcon />
                                <span className={styles.fileTypeThumbLabel}>
                                  {file.name || "Click to view"}
                                </span>
                              </a>
                            )}
                          </span>

                          <div className="flex flex-col gap-4 min-w-0">
                            <div className="flex flex-col gap-2">
                              <label className={styles.label}>Label:</label>
                              <p className={styles.labelValue}>{file.label}</p>
                            </div>
                            {file.createdAt && (
                              <div className="flex flex-col gap-2">
                                <label className={styles.label}>
                                  Uploaded:
                                </label>
                                <p className={styles.labelValue}>
                                  {formatDateTime24h(file.createdAt)}
                                </p>
                              </div>
                            )}
                            <div className="md:flex hidden flex-col gap-2 ">
                              <label className={styles.label}>
                                Additional Notes:
                              </label>
                              <div
                                className={styles.labelValue}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    file.additionalNotes ||
                                    "No additional notes",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex md:hidden flex-col gap-2 mt-4">
                          <label className={styles.label}>
                            Additional Notes:
                          </label>
                          <div
                            className={styles.labelValue}
                            dangerouslySetInnerHTML={{
                              __html:
                                file.additionalNotes || "No additional notes",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

        {showInspirationFiles &&
          revisionInspirations.length > 0 &&
          (() => {
            const grouped = revisionInspirations.reduce(
              (acc: Record<string, InspirationFile[]>, item) => {
                const key =
                  item.revisionTitle ||
                  (item.revisionId
                    ? `Revision ${item.revisionId}`
                    : "Revision");
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
              },
              {},
            );
            return (
              <div className={styles.section}>
                <CustomPageHeading
                  title="Client-Submitted Files (Per Revision)"
                  size="medium"
                />
                {Object.entries(grouped).map(([revTitle, items]) => (
                  <div
                    key={revTitle}
                    className={styles.revisionInspirationGroup}
                  >
                    <h4 className={styles.revisionGroupTitle}>{revTitle}</h4>
                    <div
                      className={classNames(
                        styles.inspirationFilesList,
                        "grid md:grid-cols-2 grid-cols-1 lg:gap-6 md:gap-5 gap-4 mt-3",
                      )}
                    >
                      {items.map((file) => (
                        <div
                          key={file.id}
                          className={classNames(
                            styles.inspirationFileItem,
                            "",
                          )}
                        >
                          <div className="flex flex-col">
                            <div className="flex md:items-start items-center gap-4">
                              <span className={styles.imgContainer}>
                                {isImageFile(file) ? (
                                  <img
                                    src={file.image}
                                    alt={file.label}
                                    onClick={() =>
                                      openImageGallery(items, file)
                                    }
                                    style={{ cursor: "pointer" }}
                                  />
                                ) : (
                                  <a
                                    href={file.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={styles.fileTypeThumb}
                                    title="Click to view file"
                                  >
                                    <Icons.FilePdfIcon />
                                    <span className={styles.fileTypeThumbLabel}>
                                      {file.name || "Click to view"}
                                    </span>
                                  </a>
                                )}
                              </span>
                              <div className="flex flex-col gap-4 min-w-0">
                                <div className="flex flex-col gap-2">
                                  <label className={styles.label}>
                                    Label:
                                  </label>
                                  <p className={styles.labelValue}>
                                    {file.label}
                                  </p>
                                </div>
                                {file.createdAt && (
                                  <div className="flex flex-col gap-2">
                                    <label className={styles.label}>
                                      Uploaded:
                                    </label>
                                    <p className={styles.labelValue}>
                                      {formatDateTime24h(file.createdAt)}
                                    </p>
                                  </div>
                                )}
                                <div className="flex flex-col gap-2">
                                  <label className={styles.label}>
                                    Additional Notes:
                                  </label>
                                  <div
                                    className={styles.labelValue}
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        file.additionalNotes ||
                                        "No additional notes",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
      </div>
      <CustomGalleryView
        show={galleryOpen}
        handleClose={() => setGalleryOpen(false)}
        items={galleryItems}
        initialIndex={galleryInitialIndex}
      />
    </div>
  );
};

export default ProjectOverview;
